import React, { createContext, useContext, useState, useEffect } from 'react';
import { 
  onAuthStateChanged, 
  signOut as firebaseSignOut,
  sendEmailVerification,
  updateProfile
} from 'firebase/auth';
import { auth } from '../../firebase/firebase';
import { initializeUserProfile } from '../../services/userService';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [currentUser, setCurrentUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const lastInitializedUserId = React.useRef(null);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      setCurrentUser(user);
      
      // Initialize user profile in Firestore only when:
      // 1. User logs in (user exists but we haven't initialized this user yet)
      // 2. User changes (different user ID)
      // This prevents redundant calls on token refresh or re-renders
      if (user && user.uid !== lastInitializedUserId.current) {
        try {
          await initializeUserProfile(user);
          lastInitializedUserId.current = user.uid;
        } catch (error) {
          console.error('Error initializing user profile:', error);
          // Don't block authentication if profile initialization fails
        }
      } else if (!user) {
        // User logged out, reset the tracking
        lastInitializedUserId.current = null;
      }
      
      setLoading(false);
    });

    return unsubscribe;
  }, []);

  const signOut = async () => {
    try {
      await firebaseSignOut(auth);
    } catch (error) {
      console.error('Error signing out:', error);
      throw error;
    }
  };

  const sendVerificationEmail = async () => {
    if (currentUser && !currentUser.emailVerified) {
      try {
        await sendEmailVerification(currentUser);
        return true;
      } catch (error) {
        console.error('Error sending verification email:', error);
        throw error;
      }
    }
    return false;
  };

  const updateUserProfile = async (displayName, photoURL = null) => {
    if (currentUser) {
      try {
        await updateProfile(currentUser, {
          displayName,
          photoURL
        });
        return true;
      } catch (error) {
        console.error('Error updating profile:', error);
        throw error;
      }
    }
    return false;
  };

  const value = {
    currentUser,
    signOut,
    sendVerificationEmail,
    updateUserProfile,
    isEmailVerified: currentUser?.emailVerified || false
  };

  return (
    <AuthContext.Provider value={value}>
      {!loading && children}
    </AuthContext.Provider>
  );
}; 